import { Signal } from "signals";
import { MaxBetMessageCommand } from "slotworks/commands/betbar/max-bet-message-command";
import { FindValidTotalStake } from "slotworks/components/betbar/betbar-helpers";
import { MenuSection } from "slotworks/components/betbar/menus/menu-section";
import BetOptions from "slotworks/components/betbar/vo/bet-options";
import StepperComponent from "slotworks/components/ui/stepper-component";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import { formatMoney, formatMoneyAsCurrency } from "slotworks/utils/string-formatters";
import Timer from "slotworks/utils/timer";
import settingsModel, { Jurisdiction } from "slotworks/model/settings/settings-model";
import betModel from "slotworks/model/bets/bet-model";
import currency from "slotworks/i18n/currency";
import translations from "slotworks/i18n/translations";

export default class BetSection extends MenuSection {

    protected options: BetOptions;

    private totalStakeStepper: StepperComponent;
    private stakePerLineStepper: StepperComponent;
    private coinSizeStepper: StepperComponent;

    private maxBetButton: FlexiButton;

    constructor(signal: Signal, initialValues: BetOptions, layer: Layers = Layers.MenusContent) {
        super(signal, initialValues, layer);

        // Stake stepper
        const stakeDownBtn = this.layer.getFlexiButton("stake_down");
        const stakeUpBtn = this.layer.getFlexiButton("stake_up");
        const stakePerLineLabel = this.layer.getFlexiText("stake_per_line_value");

        if (stakeDownBtn) {
            this.stakePerLineStepper = new StepperComponent(stakeDownBtn, stakeUpBtn, stakePerLineLabel);
            this.stakePerLineStepper.setValues(this.options.stakePerLineValues);
            this.stakePerLineStepper.onUpdate.add(this.stakePerLineChangeHandler);
        }

        // Coin size stepper
        const coinSizeDownBtn = this.layer.getFlexiButton("coin_size_down");
        const coinSizeUpBtn = this.layer.getFlexiButton("coin_size_up");
        const coinSizeLabel = this.layer.getFlexiText("coin_size_value");

        if (coinSizeDownBtn) {
            this.coinSizeStepper = new StepperComponent(coinSizeDownBtn, coinSizeUpBtn, coinSizeLabel);
            this.coinSizeStepper.formatFunction = formatMoneyAsCurrency;
            this.coinSizeStepper.setValues(this.options.coinSizes);
            this.coinSizeStepper.onUpdate.add(this.coinSizeChangeHandler);
        }

        // Total stake stepper
        const totalStakeDownBtn = this.layer.getFlexiButton("total_stake_down");
        const totalStakeUpBtn = this.layer.getFlexiButton("total_stake_up");
        const totalStakeLabel = this.layer.getFlexiText("total_stake_value");

        if (totalStakeDownBtn) {
            this.totalStakeStepper = new StepperComponent(totalStakeDownBtn, totalStakeUpBtn, totalStakeLabel);
            this.totalStakeStepper.formatFunction = formatMoney;
            this.totalStakeStepper.setValues(this.options.getTotalStakeValues());

            this.totalStakeStepper.onUpdate.add(this.totalStakeChangeHandler);
        }

        // Max bet button
        this.maxBetButton = this.layer.getFlexiButton("max_bet");
        if (this.maxBetButton) {
            this.maxBetButton.on(ButtonEvent.POINTER_DOWN, this.maxBetClickHandler);
        }

        this.updateSteppers();
    }

    public updateBetOptions(betOptions: BetOptions) {
        this.options = betOptions;

        this.updateSteppers();
    }

    public destroy() {
        if (this.maxBetButton) {
            this.maxBetButton.off(ButtonEvent.POINTER_DOWN, this.maxBetClickHandler);
        }

        if (this.stakePerLineStepper) {
            this.stakePerLineStepper.onUpdate.remove(this.stakePerLineChangeHandler);
        }

        if (this.coinSizeStepper) {
            this.coinSizeStepper.onUpdate.remove(this.coinSizeChangeHandler);
        }

        if (this.totalStakeStepper) {
            this.totalStakeStepper.onUpdate.remove(this.totalStakeChangeHandler);
        }

        super.destroy();
    }

    private maxBetClickHandler = () => {

        if (this.stakePerLineStepper) {
            this.stakePerLineStepper.max();
            this.options.stakePerLine = this.stakePerLineStepper.getSelectedValue();
        }

        if (this.coinSizeStepper) {
            this.coinSizeStepper.max();
            this.options.coinSize = this.coinSizeStepper.getSelectedValue();
        }

        if (this.totalStakeStepper) {
            this.totalStakeStepper.max();
            this.options.setTotalStake(this.totalStakeStepper.getSelectedValue());
        }

        MaxBetMessageCommand(this.options.getTotalStake());

        this.sendOptions();
        this.updateSteppers();
    }

    private stakePerLineChangeHandler = (value: number) => {
        this.options.stakePerLine = value;
        this.sendOptions();
        this.updateSteppers();
    }

    private coinSizeChangeHandler = (value: number) => {
        this.options.coinSize = value;
        this.sendOptions();
        this.updateSteppers();
    }

    private totalStakeChangeHandler = (value: number) => {
        FindValidTotalStake(value, this.options, this.totalStakeStepper, (newValue: number) => this.totalStakeChangeHandler(newValue), () => {
            this.options.setTotalStake(value);

            this.updateSteppers();
            this.sendOptions();
        });
    }

    private updateSteppers() {
        if (this.stakePerLineStepper) {
            this.stakePerLineStepper.setSelectedValue(this.options.stakePerLine);
        }

        if (this.coinSizeStepper) {
            this.coinSizeStepper.setSelectedValue(this.options.coinSize);
        }

        if (this.totalStakeStepper) {
            this.totalStakeStepper.setSelectedValue(this.options.getTotalStake());
        }

        Timer.setTimeout(() => {            
                const maxWinRtp = this.layer.getFlexiText("max_win_rtp");
                if(maxWinRtp) {
                    if(settingsModel.isJurisdiction(Jurisdiction.DE) && settingsModel.maxWin) {
                        const total = betModel.getTotalBet();
                        const value = total * settingsModel.maxWin / settingsModel.maxWinDelimiter;
                        const maxWin = currency.format(value);
                        const text = translations.get('max_win_rtp', { max_win: maxWin, max_win_probability: "1:" + settingsModel.maxWinProbability, rtp: settingsModel.rtp});
                        maxWinRtp.setText(text);
                    } else {
                        maxWinRtp.setVisible(false);
                    }
                }
        }, 10);
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/betbar/menus/bet-section.ts